import { Box, CircularProgress, Typography } from "@mui/material";

import type { GetActivityQuery } from "@/graphql/graphql";

import {
  activityDialogDocumentPlanDetailSx,
  activityDialogDocumentSx,
  activityDialogExpectedAnswerColumnSx,
  activityDialogExpectedAnswerItemSx,
  activityDialogExpectedAnswersListSx,
  activityDialogLoadingSx,
  activityDialogParagraphSx,
  activityDialogQuestionBlockSx,
  activityDialogQuestionQaRowPlanDetailSx,
  activityDialogQuestionQaRowSx,
  activityDialogQuestionTextSx,
  activityDialogQuestionTypeMetaSx,
} from "./ActivityDialog.styles";

export type ActivityContentStep = GetActivityQuery["activity"]["contents"][number];

type QuestionActivityContentStep = Extract<
  ActivityContentStep,
  { __typename?: "QuestionActivityContent" }
>;

type QuestionExpectedAnswer = NonNullable<QuestionActivityContentStep["expectedAnswers"]>[number];

interface QuestionBodyProps {
  steps: ActivityContentStep[];
  error?: boolean;
  showSpinner?: boolean;
  layout?: "dialog" | "planDetail";
}

const cleanContentTextForDisplay = (text: string) => {
  return text
    .replace(/<break.*?>/g, "")
    .replace(/,\s*,/g, ",")
    .trim();
};

const QuestionExpectedAnswersCell = ({
  answers,
}: {
  answers?: QuestionExpectedAnswer[] | null;
}) => {
  if (!answers || answers.length === 0) {
    return (
      <Typography component="p" sx={activityDialogExpectedAnswerColumnSx}>
        {"\u00a0"}
      </Typography>
    );
  } else if (answers.length === 1) {
    return (
      <Typography component="p" sx={activityDialogExpectedAnswerColumnSx}>
        {answers[0].expectedAnswerText}
      </Typography>
    );
  }

  return (
    <Box component="ul" sx={activityDialogExpectedAnswersListSx}>
      {answers.map((a) => (
        <Typography key={a.id} component="li" sx={activityDialogExpectedAnswerItemSx}>
          {a.expectedAnswerText}
        </Typography>
      ))}
    </Box>
  );
};

const QuestionActivityBody = ({
  expectedAnswers,
  readableText,
  questionTypeLabel,
  layout,
}: {
  expectedAnswers?: QuestionExpectedAnswer[] | null;
  readableText: string;
  questionTypeLabel: string;
  layout: "dialog" | "planDetail";
}) => (
  <>
    <Box
      sx={
        layout === "planDetail"
          ? activityDialogQuestionQaRowPlanDetailSx
          : activityDialogQuestionQaRowSx
      }
    >
      <Typography component="p" sx={activityDialogQuestionTextSx}>
        {readableText}
      </Typography>

      <QuestionExpectedAnswersCell answers={expectedAnswers} />
    </Box>

    <Typography component="p" sx={activityDialogQuestionTypeMetaSx}>
      {questionTypeLabel}
    </Typography>
  </>
);

const QuestionBody = ({ error, showSpinner, steps, layout = "dialog" }: QuestionBodyProps) => {
  const documentSx =
    layout === "planDetail" ? activityDialogDocumentPlanDetailSx : activityDialogDocumentSx;

  if (error) {
    return (
      <Typography color="error" variant="body2">
        Could not load activity content.
      </Typography>
    );
  }

  if (showSpinner) {
    return (
      <Box sx={activityDialogLoadingSx}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!steps.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No content for this activity.
      </Typography>
    );
  }

  return (
    <Box sx={documentSx} component="article">
      {steps.map((step) => {
        if (step.__typename !== "QuestionActivityContent") {
          return (
            <Box key={step.id} component="p" sx={activityDialogParagraphSx}>
              {cleanContentTextForDisplay(step.readableText)}
            </Box>
          );
        }

        const { questionTypeLabel, readableText, expectedAnswers } = step;
        return (
          <Box key={step.id} sx={activityDialogQuestionBlockSx}>
            <QuestionActivityBody
              expectedAnswers={expectedAnswers}
              readableText={cleanContentTextForDisplay(readableText)}
              questionTypeLabel={questionTypeLabel.trim()}
              layout={layout}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default QuestionBody;
